import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { PreferenceMatchReason } from '../types';

const formatReason = (reason: PreferenceMatchReason) =>
  reason.type === 'keyword' ? `keyword “${reason.value}”` : reason.value;

const reasonLabels: Record<PreferenceMatchReason['type'], string> = {
  organization: 'Organization',
  organization_type: 'Organization type',
  industry: 'Industry',
  keyword: 'Keyword',
};

interface MatchReasonsProps {
  reasons?: PreferenceMatchReason[];
  className?: string;
  variant?: 'inline' | 'panel';
}

const MatchReasons: React.FC<MatchReasonsProps> = ({
  reasons = [],
  className = '',
  variant = 'inline',
}) => {
  if (reasons.length === 0) return null;

  if (variant === 'panel') {
    return (
      <div className={`rounded-2xl border border-blue-100 bg-blue-50 px-4 py-4 ${className}`}>
        <div className="flex items-start gap-3">
          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Matches your preferences</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {reasons.map((reason, index) => (
                <span
                  key={`${reason.type}-${reason.value}-${index}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-white px-3 py-1 text-xs text-slate-600"
                >
                  <span className="font-semibold text-slate-700">{reasonLabels[reason.type]}:</span>
                  <span>{reason.type === 'keyword' ? `“${reason.value}”` : reason.value}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <p className={`text-xs leading-relaxed text-slate-500 ${className}`}>
      <span className="font-semibold text-slate-600">Matches:</span>{' '}
      {reasons.map(formatReason).join(' · ')}
    </p>
  );
};

export default MatchReasons;
